import { create } from 'zustand';
import { invokeEdgeFunction } from '../lib/supabase';
import { useStore } from './useStore';
import { ZWelcomeBackResponse, ZWelcomeBackStartResponse } from '../types/zod-schemas';

// --- Types ---

export interface IChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  /** Actions that were executed (shown as action cards) */
  executedActions?: IExecutedAction[];
  /** Actions pending user confirmation */
  pendingActions?: IPendingAction[];
}

export interface IPendingAction {
  id: string;
  toolName: string;
  args: Record<string, unknown>;
  preview: string;
  status: 'pending' | 'confirmed' | 'rejected';
}

export interface IExecutedAction {
  toolName: string;
  result: unknown;
}

export interface ISetupPlanStep {
  id: string;
  label: string;
  status: 'pending' | 'in_progress' | 'done';
  area_name?: string;
}

export interface ISetupPlan {
  uuid: string;
  steps: ISetupPlanStep[];
  status: 'draft' | 'active' | 'completed';
}

export type ChatMode = 'voice' | 'text';

export interface IWelcomeBackStatus {
  householdId: string;
  eligible: boolean;
  daysAway: number;
  overdueCount: number;
}

export type VoiceState = 'idle' | 'listening' | 'processing' | 'speaking' | 'awaiting_approval';

export interface IVoiceResponse {
  userText: string;
  assistantText: string;
  pendingActions?: IPendingAction[];
  messageId: string;
}

interface IChatState {
  isOpen: boolean;
  messages: IChatMessage[];
  isLoading: boolean;
  error: string | null;

  // Conversation persistence
  conversationId: string | null;
  setupPlan: ISetupPlan | null;
  isLoadingConversation: boolean;

  // Cancelled proposal previews queued by "Suggest changes" for the next sendMessage
  pendingCancelledPreviews: string[];

  // Welcome-back check-in for members returning after a break
  welcomeBack: IWelcomeBackStatus | null;

  // Voice mode state
  chatMode: ChatMode;
  voiceState: VoiceState;
  lastVoiceResponse: IVoiceResponse | null;
  shouldBeginRecording: boolean;
  pendingContinuation: IVoiceResponse | null;

  open: () => void;
  close: () => void;
  toggle: () => void;
  sendMessage: (content: string) => Promise<void>;
  sendVoiceMessage: (audioBlob: Blob) => Promise<void>;
  confirmActions: (messageId: string) => Promise<void>;
  rejectActions: (messageId: string) => void;
  rejectActionsWithContext: (messageId: string) => void;
  setChatMode: (mode: ChatMode) => void;
  setVoiceState: (state: VoiceState) => void;
  dismissVoiceOverlay: () => void;
  requestRecording: () => void;
  loadConversation: () => Promise<void>;
  startNewConversation: () => Promise<void>;
  checkWelcomeBack: () => Promise<void>;
  startWelcomeBack: () => Promise<void>;
  dismissWelcomeBack: () => void;
  reset: () => void;
}

// --- Store ---

export const useChatStore = create<IChatState>()((set, get) => ({
  isOpen: false,
  messages: [],
  isLoading: false,
  error: null,

  // Conversation persistence
  conversationId: null,
  setupPlan: null,
  isLoadingConversation: false,

  pendingCancelledPreviews: [],

  welcomeBack: null,

  // Voice mode defaults
  chatMode: 'voice',
  voiceState: 'idle',
  lastVoiceResponse: null,
  shouldBeginRecording: false,
  pendingContinuation: null,

  open: () => {
    set({ isOpen: true });
    // Load conversation if we don't have one yet
    if (!get().conversationId && !get().isLoadingConversation) {
      get().loadConversation();
    }
  },
  close: () => set({ isOpen: false }),
  toggle: () => {
    const wasOpen = get().isOpen;
    set({ isOpen: !wasOpen });
    if (!wasOpen && !get().conversationId && !get().isLoadingConversation) {
      get().loadConversation();
    }
  },
  setChatMode: (mode) => set({ chatMode: mode }),
  setVoiceState: (state) => set({ voiceState: state }),
  dismissVoiceOverlay: () => set({ voiceState: 'idle', lastVoiceResponse: null }),
  requestRecording: () => set({ shouldBeginRecording: true }),

  loadConversation: async () => {
    const householdId = useStore.getState().currentHouseholdId;
    if (!householdId) return;

    set({ isLoadingConversation: true });

    try {
      const response = await invokeEdgeFunction(`chat?household_id=${householdId}`, {
        method: 'GET',
      });

      if (!response.ok) {
        set({ isLoadingConversation: false });
        return;
      }

      const data = await response.json();

      if (data === null) {
        // No active conversation
        set({ isLoadingConversation: false, conversationId: null, messages: [], setupPlan: null });
        return;
      }

      // Restore conversation state
      interface ServerMessage {
        id: string;
        role: string;
        content: string;
        timestamp: number;
        pendingActions?: Array<{ id: string; toolName: string; args: Record<string, unknown>; preview: string }>;
        executedActions?: IExecutedAction[];
      }

      const restoredMessages: IChatMessage[] = (data.messages || []).map(
        (m: ServerMessage) => ({
          id: m.id,
          role: m.role as 'user' | 'assistant',
          content: m.content,
          timestamp: m.timestamp,
          executedActions: m.executedActions,
          pendingActions: m.pendingActions?.map((a) => ({
            ...a,
            // Restored actions from DB are already confirmed (user already acted on them)
            status: 'confirmed' as const,
          })),
        })
      );

      set({
        isLoadingConversation: false,
        conversationId: data.conversation_id,
        messages: restoredMessages,
        setupPlan: data.setup_plan || null,
      });
    } catch {
      set({ isLoadingConversation: false });
    }
  },

  startNewConversation: async () => {
    const householdId = useStore.getState().currentHouseholdId;
    const conversationId = get().conversationId;
    if (!householdId) return;

    try {
      if (conversationId) {
        await invokeEdgeFunction('chat/clear', {
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            household_id: householdId,
            conversation_id: conversationId,
          }),
        });
      }

      set({
        conversationId: null,
        messages: [],
        setupPlan: null,
        isLoading: false,
        error: null,
      });
    } catch {
      // Silently fail — user can retry
    }
  },

  checkWelcomeBack: async () => {
    const householdId = useStore.getState().currentHouseholdId;
    if (!householdId) return;

    try {
      const response = await invokeEdgeFunction(`chat/welcome-back?household_id=${householdId}`, {
        method: 'GET',
      });
      if (!response.ok) return;
      const data = ZWelcomeBackResponse.parse(await response.json());
      set({
        welcomeBack: {
          householdId,
          eligible: data.eligible,
          daysAway: data.days_away,
          overdueCount: data.overdue_count,
        },
      });
    } catch (err) {
      console.error('Failed to check welcome-back status:', err);
    }
  },

  startWelcomeBack: async () => {
    const householdId = useStore.getState().currentHouseholdId;
    if (!householdId) return;

    set({
      welcomeBack: null,
      chatMode: 'text',
      isOpen: true,
      messages: [],
      conversationId: null,
      setupPlan: null,
      isLoading: true,
      // Keeps open() from loading the previous conversation while this starts.
      isLoadingConversation: true,
      error: null,
      pendingCancelledPreviews: [],
    });

    try {
      const response = await invokeEdgeFunction('chat/welcome-back', {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ household_id: householdId }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Request failed' }));
        throw new Error(err.error || `HTTP ${response.status}`);
      }

      const data = ZWelcomeBackStartResponse.parse(await response.json());
      const assistantMessage: IChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: data.message,
        timestamp: Date.now(),
        pendingActions: data.pending_actions?.map((a) => ({ ...a, status: 'pending' as const })),
      };

      set({
        messages: [assistantMessage],
        isLoading: false,
        isLoadingConversation: false,
        conversationId: data.conversation_id,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Something went wrong';
      set({ isLoading: false, isLoadingConversation: false, error: message });
    }
  },

  dismissWelcomeBack: () => set({ welcomeBack: null }),

  sendMessage: async (content: string) => {
    const householdId = useStore.getState().currentHouseholdId;
    if (!householdId) return;

    // Auto-reject any pending actions and collect their previews for context
    const cancelledPreviews: string[] = [...get().pendingCancelledPreviews];
    const currentMessages = get().messages;
    let messagesAfterReject = currentMessages;

    for (const msg of currentMessages) {
      if (msg.pendingActions?.some((a) => a.status === 'pending')) {
        for (const action of msg.pendingActions) {
          if (action.status === 'pending') {
            cancelledPreviews.push(action.preview);
          }
        }
        messagesAfterReject = messagesAfterReject.map((m) =>
          m.id === msg.id
            ? {
                ...m,
                pendingActions: m.pendingActions?.map((a) =>
                  a.status === 'pending' ? { ...a, status: 'rejected' as const } : a
                ),
              }
            : m
        );
      }
    }

    // Add user message
    const userMessage: IChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content,
      timestamp: Date.now(),
    };

    set({
      messages: [...messagesAfterReject, userMessage],
      isLoading: true,
      error: null,
      pendingCancelledPreviews: [],
    });

    try {
      const response = await invokeEdgeFunction('chat', {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          household_id: householdId,
          message: content,
          conversation_id: get().conversationId || undefined,
          cancelled_proposals: cancelledPreviews.length > 0 ? cancelledPreviews : undefined,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Request failed' }));
        throw new Error(err.error || `HTTP ${response.status}`);
      }

      const data = await response.json();

      const assistantMessage: IChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: data.message || '',
        timestamp: Date.now(),
        executedActions: data.executed_actions,
        pendingActions: data.pending_actions?.map(
          (a: { id: string; toolName: string; args: Record<string, unknown>; preview: string }) => ({
            ...a,
            status: 'pending' as const,
          })
        ),
      };

      set({
        messages: [...get().messages, assistantMessage],
        isLoading: false,
        conversationId: data.conversation_id || get().conversationId,
        setupPlan: data.setup_plan || get().setupPlan,
      });

      // If there were executed write actions, refresh the main store
      if (data.executed_actions?.length) {
        const hasWriteAction = data.executed_actions.some(
          (a: IExecutedAction) =>
            ['create_area', 'create_task', 'create_tasks_batch', 'update_task', 'complete_task', 'fresh_start_tasks'].includes(a.toolName)
        );
        if (hasWriteAction) {
          useStore.getState().fetchHouseholdData(householdId);
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Something went wrong';
      set({ isLoading: false, error: message });
    }
  },

  sendVoiceMessage: async (audioBlob: Blob) => {
    set({ voiceState: 'processing', error: null });

    try {
      // Step 1: Transcribe audio
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      const transcribeResponse = await invokeEdgeFunction('transcribe', {
        body: formData,
      });

      if (!transcribeResponse.ok) {
        const err = await transcribeResponse.json().catch(() => ({ error: 'Transcription failed' }));
        throw new Error(err.error || `HTTP ${transcribeResponse.status}`);
      }

      const { text } = await transcribeResponse.json();
      if (!text || text.trim().length === 0) {
        set({ voiceState: 'idle' });
        return;
      }

      // Step 2: Send transcribed text through the normal chat pipeline
      const messageCountBefore = get().messages.length;
      await get().sendMessage(text);
      const messagesAfter = get().messages;

      // Find the latest assistant message added since we called sendMessage
      const newMessages = messagesAfter.slice(messageCountBefore);
      const assistantMsg = newMessages.find((m) => m.role === 'assistant');

      set({
        lastVoiceResponse: {
          userText: text,
          assistantText: assistantMsg?.content || '',
          pendingActions: assistantMsg?.pendingActions,
          messageId: assistantMsg?.id || '',
        },
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Voice message failed';
      set({ voiceState: 'idle', error: message });
    }
  },

  confirmActions: async (messageId: string) => {
    const householdId = useStore.getState().currentHouseholdId;
    if (!householdId) return;

    const message = get().messages.find((m) => m.id === messageId);
    if (!message?.pendingActions) return;

    const pendingOnes = message.pendingActions.filter((a) => a.status === 'pending');
    if (pendingOnes.length === 0) return;

    set({ isLoading: true, error: null });

    try {
      const response = await invokeEdgeFunction('chat/confirm', {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          household_id: householdId,
          actions: pendingOnes.map((a) => ({
            toolName: a.toolName,
            args: a.args,
          })),
          conversation_id: get().conversationId || undefined,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Confirmation failed' }));
        throw new Error(err.error || `HTTP ${response.status}`);
      }

      const data = await response.json();

      // Mark actions as confirmed
      const updatedMessages = get().messages.map((m) =>
        m.id === messageId
          ? {
              ...m,
              pendingActions: m.pendingActions?.map((a) => ({
                ...a,
                status: 'confirmed' as const,
              })),
            }
          : m
      );

      // If the server returned an AI continuation message, add it
      if (data.message) {
        const assistantMessage: IChatMessage = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: data.message,
          timestamp: Date.now(),
          executedActions: data.executed_actions,
          pendingActions: data.pending_actions?.map(
            (a: { id: string; toolName: string; args: Record<string, unknown>; preview: string }) => ({
              ...a,
              status: 'pending' as const,
            })
          ),
        };
        updatedMessages.push(assistantMessage);
      }

      set({
        messages: updatedMessages,
        isLoading: false,
        setupPlan: data.setup_plan || get().setupPlan,
      });

      // Signal voice continuation if the server returned one and voice is still active
      if (data.message && get().voiceState === 'processing') {
        const assistantMessage = updatedMessages[updatedMessages.length - 1];
        set({
          pendingContinuation: {
            userText: '',
            assistantText: data.message,
            pendingActions: assistantMessage.pendingActions,
            messageId: assistantMessage.id,
          },
        });
      } else if (get().voiceState === 'processing') {
        // No continuation — voice was active, go idle
        set({ voiceState: 'idle', lastVoiceResponse: null });
      }

      // Refresh main store
      useStore.getState().fetchHouseholdData(householdId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Confirmation failed';
      set({ isLoading: false, error: message });
      if (get().voiceState === 'processing') {
        set({ voiceState: 'idle', lastVoiceResponse: null });
      }
    }
  },

  rejectActions: (messageId: string) => {
    set({
      messages: get().messages.map((m) =>
        m.id === messageId
          ? {
              ...m,
              pendingActions: m.pendingActions?.map((a) => ({
                ...a,
                status: 'rejected' as const,
              })),
            }
          : m
      ),
    });

    // Sync voice overlay state so ConfirmationCard reflects the rejected status
    const current = get().lastVoiceResponse;
    if (current?.messageId === messageId) {
      set({
        lastVoiceResponse: {
          ...current,
          pendingActions: current.pendingActions?.map((a) => ({
            ...a,
            status: 'rejected' as const,
          })),
        },
      });
    }
  },

  rejectActionsWithContext: (messageId: string) => {
    // Collect previews from pending actions before rejecting
    const msg = get().messages.find((m) => m.id === messageId);
    const previews = msg?.pendingActions
      ?.filter((a) => a.status === 'pending')
      .map((a) => a.preview) || [];

    // Reject the actions visually
    get().rejectActions(messageId);

    // Queue previews so the next sendMessage includes them as cancelled_proposals
    if (previews.length > 0) {
      set({ pendingCancelledPreviews: [...get().pendingCancelledPreviews, ...previews] });
    }
  },

  reset: () =>
    set({
      isOpen: false,
      messages: [],
      isLoading: false,
      error: null,
      conversationId: null,
      setupPlan: null,
      isLoadingConversation: false,
      pendingCancelledPreviews: [],
      welcomeBack: null,
      chatMode: 'voice',
      voiceState: 'idle',
      lastVoiceResponse: null,
      shouldBeginRecording: false,
      pendingContinuation: null,
    }),
}));
